import { Address } from '../../../interop/webmodule-interop';
import { ButtonEventMap, RowEventMap } from './input-type';
import { emptyAddress } from '../maps/map-helpers';
import { FormInputSelectValue } from './form-input-types';
import { getToolTip } from '../../tooltip';
import { html, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { WebmoduleButtonSize, WebmoduleButtonVariant } from '../../../components/src/components/button/button';
import { when } from 'lit/directives/when.js';

/** Options for {@link FormInputAssistantBase.combobox}. */
export interface ComboboxOptions {
  /** Element id - set by the data-bound `comboboxField` helper so the data-binder can find it. */
  id?: string;
  /** Field label (left-aligned, like the other form controls). */
  label?: string | TemplateResult;
  /** Tooltip shown next to the label. */
  toolTip?: string | TemplateResult;
  /** The control's value (selected option value, or created text). */
  value?: string;
  /** The selectable options. */
  values: FormInputSelectValue[];
  /** Lets the user create a new option by typing a value that matches none of the options. */
  allowCreate?: boolean;
  /** Customises the "Create ..." row text. */
  getCreateLabel?: (value: string) => string;
  /** Text shown when there are no matching options (and nothing to create). Defaults to "No results". */
  emptyText?: string;
  /** Shows a clear button (to reset the value) when the control is not empty. */
  clearable?: boolean;
  /** Placeholder shown when empty. */
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  readonly?: boolean;
  class?: string;
  slot?: string;
  events?: {
    change?: (e: Event) => void;
    create?: (e: CustomEvent<{ value: string }>) => void;
    input?: (e: Event) => void;
    focus?: (e: Event) => void;
    blur?: (e: Event) => void;
  };
}

export type ButtonSlot = 'suffix' | 'prefix';

export interface ButtonOptions {
  events?: ButtonEventMap;
  readonly?: boolean;
  slot?: ButtonSlot;
  class?: string;
  size?: WebmoduleButtonSize;
}

interface ButtonOptionsInternal {
  events?: ButtonEventMap;
  readonly?: boolean;
  slot?: ButtonSlot;
  variant?: WebmoduleButtonVariant;
  size?: WebmoduleButtonSize;
  class?: string;
}

interface AddressOptions {
  onAddressChanged: (e: Event) => Promise<void>;
  address?: Address;
  shippingNotes?: string;
  readonly?: boolean;
  isDefaultShippingVisible?: boolean;
  isDefaultShipping?: boolean;
  displayMap?: boolean;
}

export class FormInputAssistantBase {
  toolTipMaxSize?: string;

  constructor(forceReadonly: boolean | (() => boolean) = false) {
    this._forceReadonly = typeof forceReadonly === 'function' ? forceReadonly : () => forceReadonly;
  }

  private _forceReadonly: () => boolean;

  get forceReadonly(): boolean {
    return this._forceReadonly();
  }

  public clickableIcon(options: {
    events: ButtonEventMap;
    library: string;
    name: string;
    class?: string;
    slot?: string;
  }) {
    return html` <webmodule-icon-button
      class="${options.class ?? ''}"
      @click=${options.events.click}
      slot=${options.slot ?? ''}
      library=${options.library}
      name=${options.name}
    ></webmodule-icon-button>`;
  }

  public buttonPrimary(caption: string | TemplateResult, options: ButtonOptions) {
    return this.buttonInternal(caption, { ...options, variant: 'primary' });
  }

  public buttonDefault(caption: string | TemplateResult, options: ButtonOptions) {
    return this.buttonInternal(caption, { ...options, variant: 'default' });
  }

  public buttonDanger(caption: string | TemplateResult, options: ButtonOptions) {
    return this.buttonInternal(caption, { ...options, variant: 'danger' });
  }

  public buttonWarning(caption: string | TemplateResult, options: ButtonOptions) {
    return this.buttonInternal(caption, { ...options, variant: 'warning' });
  }

  public buttonSuccess(caption: string | TemplateResult, options: ButtonOptions) {
    return this.buttonInternal(caption, { ...options, variant: 'success' });
  }

  public addressEditor(title: string, id: string, options: AddressOptions) {
    return this.addressInternal(title, id, options);
  }

  public multiEditRow(options: {
    title?: string;
    toolTip?: string | TemplateResult;
    slotted: TemplateResult;
    hidden?: boolean;
    events?: RowEventMap;
  }) {
    return options?.hidden
      ? html`<span class="d-none">${options.slotted}</span>`
      : html` <webmodule-input-row
          size="small"
          class="webmodule-control-left-label"
          @mouseleave=${options?.events?.mouseleave}
          @click=${options?.events?.click}
          @mouseenter=${options?.events?.mouseenter}
        >
          ${options.slotted}
          ${when(
            options.title,
            () => html`
              <span slot="label">
                ${options.title} ${options.toolTip ? getToolTip(options.toolTip, { maxSize: this.toolTipMaxSize }) : ''}
              </span>
            `
          )}
        </webmodule-input-row>`;
  }

  /**
   * A combobox form control (filterable input + listbox, with optional create-on-type). Centralised
   * here so the underlying component (`webmodule-combobox`) can be swapped in one place. The
   * left-label layout and sizing match the other form-control helpers.
   */
  public combobox(options: ComboboxOptions): TemplateResult {
    const readonly = (options.readonly ?? false) || this.forceReadonly;
    return html` <webmodule-combobox
      id=${ifDefined(options.id)}
      class="webmodule-control-left-label ${options.class ?? ''}"
      size=${options.size ?? 'small'}
      placeholder=${options.placeholder ?? ''}
      ?allow-create=${options.allowCreate ?? false}
      ?clearable=${options.clearable ?? false}
      ?disabled=${readonly}
      slot=${options.slot ?? ''}
      empty-text=${ifDefined(options.emptyText)}
      .value=${options.value ?? ''}
      .options=${options.values}
      .getCreateLabel=${options.getCreateLabel ?? ((value: string) => `Create "${value}"`)}
      @webmodule-change=${options.events?.change}
      @webmodule-create=${options.events?.create}
      @webmodule-input=${options.events?.input}
      @webmodule-focus=${options.events?.focus}
      @webmodule-blur=${options.events?.blur}
    >
      ${when(
        options.label,
        () => html`
          <span slot="label">
            ${options.label} ${options.toolTip ? getToolTip(options.toolTip, { maxSize: this.toolTipMaxSize }) : ''}
          </span>
        `
      )}
    </webmodule-combobox>`;
  }

  protected buttonInternal(caption: string | TemplateResult, options?: ButtonOptionsInternal) {
    return html` <webmodule-button
      ?disabled=${(options?.readonly ?? false) || this.forceReadonly}
      .variant=${options?.variant ?? 'default'}
      @click=${options?.events?.click}
      slot=${options?.slot ?? ''}
      size=${options?.size ?? 'medium'}
      class=${options?.class ?? ''}
      >${caption}
    </webmodule-button>`;
  }

  protected addressInternal(title: string, id: string, options: AddressOptions) {
    return html` <wm-addresseditor
      @wm-ae-changed=${options.onAddressChanged}
      id=${id}
      .address=${options.address ?? emptyAddress()}
      .shippingNotes=${options.shippingNotes}
      .sectionTitle=${title}
      .readonly=${options.readonly ?? false}
      .isDefaultShippingVisible=${options.isDefaultShippingVisible ?? false}
      .isDefaultShipping=${options.isDefaultShipping ?? false}
      .displayMap=${options.displayMap ?? true}
    >
    </wm-addresseditor>`;
  }
}
